import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Cast, Plus, Trash2, Youtube, Facebook, Twitch, Globe, Eye, EyeOff, Copy, Check, Power } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

function Simulcast({ user, onLogout }) {
  const { t } = useLanguage();
  const [simulcasts, setSimulcasts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    channel_id: '',
    platform: 'youtube',
    rtmp_url: '',
    stream_key: ''
  });
  const [showStreamKey, setShowStreamKey] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const platformDefaults = {
    youtube: {
      name: 'YouTube Live',
      rtmp_url: 'rtmp://a.rtmp.youtube.com/live2',
      icon_color: '#FF0000',
      gradient: 'from-red-500 to-red-600',
      help: 'Obtén tu stream key desde YouTube Studio > En vivo'
    },
    facebook: {
      name: 'Facebook Live',
      rtmp_url: 'rtmps://live-api-s.facebook.com:443/rtmp',
      icon_color: '#1877F2',
      gradient: 'from-blue-500 to-blue-600',
      help: 'Obtén tu stream key desde Creator Studio > Video en vivo'
    },
    twitch: {
      name: 'Twitch',
      rtmp_url: 'rtmp://live.twitch.tv/app',
      icon_color: '#9146FF',
      gradient: 'from-purple-500 to-purple-600',
      help: 'Obtén tu stream key desde el Dashboard de Twitch'
    },
    tiktok: {
      name: 'TikTok Live',
      rtmp_url: 'rtmp://push.live.tiktok.com/live/',
      icon_color: '#000000',
      gradient: 'from-gray-800 to-black',
      help: 'Obtén tu stream key desde TikTok Live Studio'
    },
    instagram: {
      name: 'Instagram Live',
      rtmp_url: 'rtmps://live-upload.instagram.com:443/rtmp/',
      icon_color: '#E4405F',
      gradient: 'from-pink-500 to-purple-500',
      help: 'Obtén tu stream key desde Instagram Professional Dashboard'
    },
    custom: {
      name: 'Custom RTMP',
      rtmp_url: '',
      icon_color: '#6366f1',
      gradient: 'from-accent-indigo to-accent-purple',
      help: 'Ingresa la URL RTMP completa de tu servidor personalizado'
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [simulcastRes, channelsRes] = await Promise.all([
        axios.get('/api/simulcast', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/channels', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSimulcasts(simulcastRes.data.simulcasts || []);
      setChannels(channelsRes.data.channels || []);
      setLoading(false);
    } catch (error) {
      toast.error('Error al cargar datos');
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.rtmp_url && formData.platform !== 'custom') {
      toast.error('URL RTMP es requerida');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/simulcast', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Simulcast configurado exitosamente');
      setShowModal(false);
      setFormData({ channel_id: '', platform: 'youtube', rtmp_url: '', stream_key: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al configurar simulcast');
    }
  };

  const handleToggleEnabled = async (simulcast) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/simulcast/${simulcast.id}`, {
        enabled: !simulcast.enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(simulcast.enabled ? 'Simulcast deshabilitado' : 'Simulcast habilitado');
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handlePlatformChange = (platform) => {
    const defaults = platformDefaults[platform];
    setFormData({
      ...formData,
      platform,
      rtmp_url: defaults.rtmp_url
    });
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleShowKey = (id) => {
    setShowStreamKey(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta configuración?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/simulcast/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getPlatformIcon = (platform, size = 24) => {
    const color = platformDefaults[platform]?.icon_color || '#6366f1';
    switch (platform) {
      case 'youtube': return <Youtube size={size} color={color} />;
      case 'facebook': return <Facebook size={size} color={color} />;
      case 'twitch': return <Twitch size={size} color={color} />;
      case 'tiktok': return <Cast size={size} color={color} />;
      case 'instagram': return <Cast size={size} color={color} />;
      case 'custom': return <Globe size={size} color={color} />;
      default: return <Cast size={size} color={color} />;
    }
  };

  const getPlatformName = (platform) => {
    return platformDefaults[platform]?.name || platform;
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center items-center h-screen">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-accent-indigo rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-2.5 min-h-screen max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1">
              Destinos de Stream
            </h1>
            <p className="text-gray-400 text-sm">
              Gestiona y configura tus plataformas de retransmisión multicanal.
            </p>
          </div>
          <button
            className="btn-primary-gradient flex-shrink-0"
            onClick={() => setShowModal(true)}>
            <Plus size={18} />
            Añadir Destino
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {simulcasts.map((simulcast) => (
            <div key={simulcast.id} className="rounded-2xl border border-white/10 bg-[#1a1f2e] p-6 transition-all duration-300">
              {/* Card header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                    {getPlatformIcon(simulcast.platform, 28)}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {getPlatformName(simulcast.platform)}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {simulcast.channel_name
                        ? (simulcast.platform === 'youtube' ? `Canal: ${simulcast.channel_name}` :
                           simulcast.platform === 'facebook' ? `Stream ID: ${simulcast.channel_name}` :
                           simulcast.channel_name)
                        : 'Sin canal asignado'}
                    </p>
                  </div>
                </div>
                <button
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/5 hover:bg-red-600/20 text-gray-400 hover:text-red-400 border border-white/10 hover:border-red-600/30 transition-all duration-200"
                  onClick={() => handleDelete(simulcast.id)}>
                  <Trash2 size={16} />
                </button>
              </div>

              {/* URL RTMP field */}
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <Globe size={11} className="text-gray-500" />
                  <span className="text-[11px] font-semibold text-gray-500 tracking-widest uppercase">URL RTMP</span>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm font-mono text-gray-300 overflow-hidden text-ellipsis whitespace-nowrap">
                    {simulcast.rtmp_url}
                  </div>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10 transition-all duration-200 flex-shrink-0"
                    onClick={() => handleCopy(simulcast.rtmp_url, `url-${simulcast.id}`)}>
                    {copiedId === `url-${simulcast.id}` ? <Check size={14} className="text-accent-emerald" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>

              {/* Stream Key field */}
              <div className="mb-5">
                <div className="flex items-center gap-1.5 mb-2">
                  <Cast size={11} className="text-gray-500" />
                  <span className="text-[11px] font-semibold text-gray-500 tracking-widest uppercase">Stream Key</span>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm font-mono text-gray-300 overflow-hidden text-ellipsis whitespace-nowrap">
                    {showStreamKey[simulcast.id] ? simulcast.stream_key : '• '.repeat(10).trim()}
                  </div>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10 transition-all duration-200 flex-shrink-0"
                    onClick={() => toggleShowKey(simulcast.id)}>
                    {showStreamKey[simulcast.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10 transition-all duration-200 flex-shrink-0"
                    onClick={() => handleCopy(simulcast.stream_key, `key-${simulcast.id}`)}>
                    {copiedId === `key-${simulcast.id}` ? <Check size={14} className="text-accent-emerald" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>

              {/* Action row */}
              <div className="flex gap-3 items-center">
                <button
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all duration-200 shadow-lg ${
                    simulcast.enabled
                      ? 'bg-red-600 hover:bg-red-700 shadow-red-600/20'
                      : 'bg-green-600 hover:bg-green-700 shadow-green-600/20'
                  }`}
                  onClick={() => handleToggleEnabled(simulcast)}>
                  <Power size={16} />
                  {simulcast.enabled ? 'Detener Destino' : 'Habilitar Destino'}
                </button>
                <div className={`px-4 py-3 rounded-xl font-semibold text-sm flex-shrink-0 ${
                  simulcast.enabled
                    ? 'bg-green-900/40 text-green-400 border border-green-600/30'
                    : 'bg-amber-900/40 text-amber-500 border border-amber-600/30'
                }`}>
                  {simulcast.enabled ? 'ACTIVO' : 'INACTIVO'}
                </div>
              </div>
            </div>
          ))}

          {/* Añadir Nuevo Destino card */}
          <div
            className="rounded-2xl border-2 border-dashed border-black/15 dark:border-white/15 hover:border-accent-purple/40 p-6 flex flex-col items-center justify-center cursor-pointer min-h-[280px] group transition-all duration-300 hover:bg-accent-purple/5"
            onClick={() => setShowModal(true)}
          >
            <div className="w-16 h-16 rounded-full border-2 border-black/15 dark:border-white/20 group-hover:border-accent-purple/50 flex items-center justify-center mb-5 transition-all duration-300">
              <Plus size={28} className="text-gray-400 group-hover:text-accent-purple transition-colors" />
            </div>
            <h3 className="text-base font-semibold text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors mb-2">
              Añadir Nuevo Destino
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-600 text-center leading-relaxed">
              Configura Twitch, Twitter u otros servidores RTMP personalizados
            </p>
          </div>
        </div>

        {showModal && (
          <div className="modal-overlay-premium"
               onClick={() => setShowModal(false)}>
            <div className="modal-premium max-w-2xl"
                 onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold mb-6 text-white flex items-center gap-3">
                <Cast size={28} className="text-accent-purple" />
                Agregar Plataforma
              </h2>
              <form onSubmit={handleCreate}>
                <div className="mb-4">
                  <label className="block mb-2 font-semibold text-white flex items-center gap-2">
                    <Cast size={16} className="text-accent-indigo" />
                    Canal
                  </label>
                  <select className="input-glass"
                          value={formData.channel_id}
                          onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
                          required>
                    <option value="">Seleccionar canal</option>
                    {channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>{channel.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block mb-2 font-semibold text-white flex items-center gap-2">
                    <Globe size={16} className="text-accent-purple" />
                    Plataforma
                  </label>
                  <select className="input-glass"
                          value={formData.platform}
                          onChange={(e) => handlePlatformChange(e.target.value)}>
                    <option value="youtube">YouTube Live</option>
                    <option value="facebook">Facebook Live</option>
                    <option value="twitch">Twitch</option>
                    <option value="tiktok">TikTok Live</option>
                    <option value="instagram">Instagram Live</option>
                    <option value="custom">Custom RTMP</option>
                  </select>
                  {platformDefaults[formData.platform]?.help && (
                    <p className="text-xs text-gray-400 mt-2 flex items-start gap-2">
                      <span className="text-yellow-500">💡</span>
                      {platformDefaults[formData.platform].help}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block mb-2 font-semibold text-white flex items-center gap-2">
                    <Globe size={16} className="text-accent-indigo" />
                    URL del Servidor RTMP
                  </label>
                  <input type="text"
                         className="input-glass font-mono text-sm"
                         value={formData.rtmp_url}
                         onChange={(e) => setFormData({ ...formData, rtmp_url: e.target.value })}
                         placeholder={formData.platform === 'custom' ? 'rtmp://tu-servidor.com/live' : platformDefaults[formData.platform]?.rtmp_url}
                         required />
                  <p className="text-xs text-gray-400 mt-2">
                    {formData.platform === 'custom'
                      ? 'Ingresa la URL RTMP completa de tu servidor personalizado'
                      : 'URL predeterminada para ' + getPlatformName(formData.platform)}
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block mb-2 font-semibold text-white flex items-center gap-2">
                    <Cast size={16} className="text-accent-purple" />
                    Stream Key
                  </label>
                  <input type="text"
                         className="input-glass font-mono text-sm"
                         value={formData.stream_key}
                         onChange={(e) => setFormData({ ...formData, stream_key: e.target.value })}
                         placeholder="Tu stream key de la plataforma"
                         required />
                  <p className="text-xs text-gray-400 mt-2">
                    Obtén tu stream key desde la configuración de transmisión de la plataforma
                  </p>
                </div>

                <div className="glass-card bg-accent-indigo/5 border-accent-indigo/20 mb-6">
                  <div className="text-sm font-semibold mb-2 text-white flex items-center gap-2">
                    <Eye size={16} className="text-accent-indigo" />
                    Vista Previa
                  </div>
                  <div className="text-xs text-gray-400 mb-2">
                    URL Completa:
                  </div>
                  <code className="block bg-black/40 px-3 py-2 rounded-lg text-xs font-mono break-all border border-accent-indigo/30 text-accent-indigo">
                    {formData.rtmp_url || 'Ingresa la URL RTMP'}/{formData.stream_key || 'stream-key'}
                  </code>
                </div>

                <div className="flex gap-4">
                  <button type="submit" className="btn-primary-gradient flex-1 flex items-center justify-center gap-2">
                    <Plus size={16} />
                    Agregar Simulcast
                  </button>
                  <button type="button" className="btn-glass flex-1"
                          onClick={() => setShowModal(false)}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Simulcast;
